import { useEffect, useMemo, useState } from "react";

import { paletteFor, topicColorResolved } from "@app/core";

import {
  PaletteCanvas,
  paletteBoardTemplateForPreset,
  type PaletteMarker,
  type PaletteSampleMarker,
  type PaletteSwatchColors,
  type PaletteTemplate,
} from "../../../palettePicker/index.js";
import { resolvedCalendarColor } from "../../../features/entity-colors.js";
import { orderByIds } from "../../../lib/apply-order.js";
import { useI18n } from "../../../lib/useI18n.js";
import {
  boardTargetKey,
  useCalendarsStore,
  useLabelsStore,
  usePaletteBoardStore,
  useSettingsStore,
  useThemeStore,
  useTopicsStore,
  type BoardTargetRef,
} from "../../../stores/index.js";
import { applyBoardTarget, boardTargetHex, boardTargetLabel, type BoardTargetActions } from "./board-targets.js";

function findSwatchIndex(template: PaletteTemplate, hex: string, swatchColors?: PaletteSwatchColors): number | null {
  const match = template.swatches.find((swatch) => {
    const swatchHex = swatchColors?.[swatch.index] ?? swatch.baseHex;
    return swatchHex.toLowerCase() === hex.toLowerCase();
  });
  return match?.index ?? null;
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
}

/** Theme-store actions plus the label write (labels live on their own record,
 *  not in the theme config). */
function boardActions(): BoardTargetActions {
  return {
    ...useThemeStore.getState(),
    setLabelColor: (id, hex) => void useLabelsStore.getState().setColor(id, hex),
  };
}

/** The decorative palette board — the page-wide color picking surface. Clicking
 *  a swatch (or pixel-sampling the artwork) paints the ARMED target: whatever
 *  color row/swatch on the page was last clicked or focused. Swatches show the
 *  ACTIVE entity palette (cycled), so the board follows the current theme. */
export function PaletteBoardPanel({ defaultTarget }: { defaultTarget?: BoardTargetRef | undefined } = {}) {
  const { t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const setBoardSample = useThemeStore((s) => s.setBoardSample);
  const topics = useTopicsStore((s) => s.topics);
  const calendars = useCalendarsStore((s) => s.calendars);
  const calendarOrder = useSettingsStore((s) => s.calendarOrder);
  const labels = useLabelsStore((s) => s.labels);
  const target = usePaletteBoardStore((s) => s.target);
  const arm = usePaletteBoardStore((s) => s.arm);
  const [debug, setDebug] = useState(false);
  const template = paletteBoardTemplateForPreset(config.preset);

  // Default arm when nothing is armed yet: the host page's choice, else the
  // first topic (the classic board use), else the global row.
  useEffect(() => {
    if (target) return;
    arm(defaultTarget
      ?? (topics[0]
        ? { kind: "entity", target: { kind: "topic", id: topics[0].id } }
        : { kind: "entity", target: { kind: "global" } }));
  }, [target, topics, arm, defaultTarget]);

  const swatchColors = useMemo<PaletteSwatchColors | undefined>(() => {
    const palette = paletteFor(config);
    if (palette.length === 0) return undefined;
    const colors: PaletteSwatchColors = {};
    for (const swatch of template.swatches) colors[swatch.index] = palette[swatch.index % palette.length]!;
    return colors;
  }, [config, template]);

  const key = target ? boardTargetKey(target) : null;
  const activeHex = target ? boardTargetHex(target, config, topics, calendars, labels) : "#888888";
  const storedSample = key ? config.entity.boardSamples?.[key] : undefined;
  const activeSample = storedSample
    && storedSample.board === template.id
    && storedSample.hex.toLowerCase() === activeHex.toLowerCase()
    ? storedSample : null;
  const selectedIndex = activeSample ? null : findSwatchIndex(template, activeHex, swatchColors);

  // Calendars in list order (top-to-bottom) — their 1-based position is shown
  // both on the Calendars-panel rows and as the badge on their palette swatch.
  const orderedCalendars = useMemo(
    () => orderByIds(calendars, calendarOrder, (calendar) => calendar.id),
    [calendars, calendarOrder],
  );

  // Non-armed topics/calendars stay visible as badges so the board doubles as
  // the entity color map: on their swatch when the color is a palette color, at
  // their sampled spot when it was pixel-sampled from the artwork. Topic badges
  // (icon/initials) sit bottom-right; calendar badges (list number) sit
  // top-left so the two don't stack when they share a swatch.
  const markers = useMemo<PaletteMarker[]>(() => {
    const next: PaletteMarker[] = [];
    topics.forEach((topic, index) => {
      if (key === `topic:${topic.id}`) return;
      const color = topicColorResolved(config, topic.id, index);
      const swatchIndex = findSwatchIndex(template, color, swatchColors);
      if (swatchIndex !== null) next.push({ index: swatchIndex, color, label: topic.icon?.trim() || initials(topic.name) });
    });
    orderedCalendars.forEach((calendar, index) => {
      if (key === `calendar:${calendar.id}`) return;
      const color = resolvedCalendarColor(config, calendars, calendar.id);
      if (!color) return;
      const swatchIndex = findSwatchIndex(template, color, swatchColors);
      if (swatchIndex !== null) next.push({ index: swatchIndex, color, label: String(index + 1), corner: "tl" });
    });
    return next;
  }, [topics, orderedCalendars, calendars, key, config, swatchColors, template]);
  const sampleMarkers = useMemo<PaletteSampleMarker[]>(() => {
    const samples = config.entity.boardSamples ?? {};
    const next: PaletteSampleMarker[] = [];
    topics.forEach((topic, index) => {
      if (key === `topic:${topic.id}`) return;
      const sample = samples[`topic:${topic.id}`];
      if (!sample || sample.board !== template.id) return;
      if (sample.hex.toLowerCase() !== topicColorResolved(config, topic.id, index).toLowerCase()) return;
      next.push({ x: sample.x, y: sample.y, hex: sample.hex, label: topic.icon?.trim() || initials(topic.name) });
    });
    orderedCalendars.forEach((calendar, index) => {
      if (key === `calendar:${calendar.id}`) return;
      const sample = samples[`calendar:${calendar.id}`];
      if (!sample || sample.board !== template.id) return;
      const color = resolvedCalendarColor(config, calendars, calendar.id);
      if (!color || sample.hex.toLowerCase() !== color.toLowerCase()) return;
      next.push({ x: sample.x, y: sample.y, hex: sample.hex, label: String(index + 1) });
    });
    return next;
  }, [topics, orderedCalendars, calendars, key, config, template]);

  const label = target
    ? boardTargetLabel(target, {
      topics,
      calendars,
      labels,
      strings: {
        global: t("theme.entity.global"),
        task: t("theme.entity.task"),
        event: t("theme.entity.event"),
        slot: (n) => t("theme.paletteBoard.slot", { n: String(n) }),
        sourceLight: t("theme.sourceLight"),
        sourceDark: t("theme.sourceDark"),
        modeLight: t("theme.mode.light"),
        modeDark: t("theme.mode.dark"),
      },
    })
    : "";

  return (
    <div className="flex flex-col gap-2">
      <PaletteCanvas
        template={template}
        texture
        debug={debug}
        selectedIndex={selectedIndex}
        swatchColors={swatchColors}
        markers={markers}
        samplePoint={activeSample ? { x: activeSample.x, y: activeSample.y, hex: activeSample.hex } : null}
        sampleMarkers={sampleMarkers}
        onSelect={({ hex }) => {
          if (!target || !key) return;
          applyBoardTarget(boardActions(), target, hex);
          setBoardSample(key, null);
        }}
        onSample={({ hex, x, y }) => {
          if (!target || !key) return;
          applyBoardTarget(boardActions(), target, hex);
          setBoardSample(key, { board: template.id, x, y, hex });
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase text-muted-foreground">{t("theme.paletteBoard.applyTo")}</span>
        <span className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-sm">
          <span className="h-4 w-4 rounded-full border border-border" style={{ background: activeHex }} aria-hidden />
          {label}
        </span>
        {activeSample && <span className="text-xs text-muted-foreground">{t("theme.paletteBoard.imageSample")}</span>}
        <p className="min-w-0 flex-1 basis-48 text-xs text-muted-foreground">{t("theme.paletteBoard.hint")}</p>
        {import.meta.env.DEV && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={debug} onChange={(event) => setDebug(event.target.checked)} />
            {t("theme.paletteBoard.debug")}
          </label>
        )}
      </div>
    </div>
  );
}
